import React, { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ProTable, ProColumns, ParamsType } from "@ant-design/pro-components";
import { Button, Typography, message, Popconfirm } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { FaEye, FaTrash } from "react-icons/fa";
import { TablePaginationConfig } from "antd/es/table";
import { FilterValue, SorterResult } from "antd/es/table/interface";
import { IComment, QueryParams } from "../comment/type/CommentManagementType";
import {
  getCommentList,
  updateIsShowComment,
  deleteComment,
} from "../comment/api/CommentAPI";
import { getDictionaryList } from "../dictionary/api/DictionaryAPI";
import { IDictionary } from "../dictionary/type/DictionaryManagementType";
import { debounce } from "lodash";

const { Text, Title } = Typography;

const CommentManagement: React.FC = () => {
  const dictionaryHostUrl = process.env.REACT_APP_DICTIONARY_HOST_URL;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const parseSearchParams = (search: string): QueryParams => {
    const params = new URLSearchParams(search);
    return {
      page: parseInt(params.get("page") || "1"),
    };
  };

  const toSearchParams = (queryParams: QueryParams): string => {
    const params = new URLSearchParams();

    if (queryParams.page) {
      params.set("page", queryParams.page.toString());
    }

    return params.toString();
  };

  const [queryParams, setQueryParams] = useState<QueryParams>({
    ...parseSearchParams(location.search),
    limit: 100,
    sortBy: "",
  });

  const [searchParams, setSearchParams] = useState<{
    dictionary?: string;
    hanji?: string;
    dateRange?: [string, string];
    user?: string;
  }>({});

  useEffect(() => {
    const searchString = toSearchParams(queryParams);

    // remove '?'
    const currentSearch = location.search.substring(1);

    if (searchString !== currentSearch) {
      navigate(`${location.pathname}?${searchString}`, { replace: true });
    }
  }, [queryParams, navigate, location.pathname]);

  const { data, isLoading } = useQuery({
    queryKey: ["comments", queryParams, searchParams],
    queryFn: () => getCommentList({ ...queryParams, ...searchParams }),
  });

  const { data: dictionaryData } = useQuery({
    queryKey: ["dictionaries"],
    queryFn: () => getDictionaryList({ params: {} }),
  });

  const updateCommentMutation = useMutation({
    mutationFn: ({
      id,
      isShowComment,
    }: {
      id: string;
      isShowComment: boolean;
    }) => updateIsShowComment(id, isShowComment),
    onSuccess: () => {
      message.success("Comment status updated successfully");
      queryClient.invalidateQueries({ queryKey: ["comments"] });
    },
    onError: () => {
      message.error("Failed to update the comment");
    },
  });

  const archiveCommentMutation = useMutation({
    mutationFn: ({ id }: { id: string }) => deleteComment(id),
    onSuccess: () => {
      message.success("Comment deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["comments"] });
    },
    onError: () => {
      message.error("Failed to delete the comment");
    },
  });

  const debouncedSetSearchParams = useCallback(
    debounce((newParams: Partial<typeof searchParams>) => {
      setSearchParams((prev) => ({ ...prev, ...newParams }));
    }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchParams.cancel();
    };
  }, [debouncedSetSearchParams]);

  const handleTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<IComment> | SorterResult<IComment>[]
    ) => {
      const sortString = Array.isArray(sorter)
        ? sorter
            .map((s) => `${s.field}:${s.order === "ascend" ? "asc" : "desc"}`)
            .join(",")
        : sorter.field && sorter.order
          ? `${sorter.field}:${sorter.order === "ascend" ? "asc" : "desc"}`
          : "";

      let dateRange: [string, string] | undefined;
      if (Array.isArray(filters.dateRange) && filters.dateRange.length === 2) {
        dateRange = filters.dateRange as [string, string];
      }

      setQueryParams((prev) => ({
        ...prev,
        page: pagination.current || 1,
        limit: pagination.pageSize || 100,
        sortBy: sortString,
      }));

      debouncedSetSearchParams({
        dictionary: filters.dictionary?.[0] as string | undefined,
        hanji: filters.hanji?.[0] as string | undefined,
        dateRange: dateRange,
      });
    },
    [debouncedSetSearchParams]
  );

  const columns: ProColumns<IComment>[] = [
    {
      title: "Source",
      dataIndex: ["word", "dictionary", "name"],
      key: "dictionary",
      valueType: "select",
      valueEnum: dictionaryData?.results.reduce(
        (acc: Record<string, { text: string }>, dict: IDictionary) => {
          acc[dict.name] = { text: dict.name };
          return acc;
        },
        {}
      ),
      fieldProps: {
        onChange: (value: string) =>
          debouncedSetSearchParams({ dictionary: value }),
      },
    },
    {
      title: "Chinese character",
      dataIndex: ["word", "hanji"],
      key: "hanji",
      valueType: "text",
      fieldProps: {
        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
          debouncedSetSearchParams({ hanji: e.target.value }),
      },
    },
    {
      title: "Romanisation",
      dataIndex: ["word", "roman"],
      key: "roman",
      search: false,
    },
    {
      title: "User",
      dataIndex: ["user", "username"],
      key: "user",
      valueType: "text",
      fieldProps: {
        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
          debouncedSetSearchParams({ user: e.target.value }),
      },
    },
    {
      title: "Date & Time",
      dataIndex: "createdAt",
      valueType: "dateRange",
      key: "dateRange",
      render: (_, record) => {
        const date = new Date(record.createdAt);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString();
      },
      sorter: true,
      fieldProps: {
        onChange: (dates: [string, string]) =>
          debouncedSetSearchParams({ dateRange: dates }),
      },
    },
    {
      title: "Comment",
      dataIndex: "comment",
      // ellipsis: true,
      search: false,
      width: 400,
      render: (_, record) => (
        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {record.comment}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "action",
      valueType: "option",
      render: (_, record: IComment) => [
        <Button
          key="view"
          icon={<span>🔗</span>}
          onClick={() => {
            window.open(
              `${dictionaryHostUrl}/search/word/${record.word?._id || ""}`,
              "_blank"
            );
          }}
        />,
        record.isShowComment ? (
          <Button
            key="hide"
            type="text"
            style={{ color: "#593C26" }}
            onClick={() => {
              updateCommentMutation.mutate({
                id: record._id,
                isShowComment: !record.isShowComment,
              });
            }}
          >
            Hide
          </Button>
        ) : (
          <Text
            key="hide-disabled"
            style={{ color: "#D0D5DD", padding: "0px 16px" }}
          >
            Hide
          </Text>
        ),
        record.isShowComment ? (
          <Text
            key="active-disabled"
            style={{ color: "#D0D5DD", padding: "0px 16px" }}
          >
            Display
          </Text>
        ) : (
          <Button
            key="active"
            type="text"
            style={{ color: "#593C26" }}
            onClick={() => {
              updateCommentMutation.mutate({
                id: record._id,
                isShowComment: !record.isShowComment,
              });
            }}
          >
            Display
          </Button>
        ),
        <Popconfirm
          title="Are you sure you want to delete this comment?"
          onConfirm={() =>
            record._id && archiveCommentMutation.mutate({ id: record._id })
          }
          okText="Yes"
          cancelText="No"
        >
          <Button icon={<FaTrash />} />
        </Popconfirm>,
      ],
    },
  ];

  return (
    <div style={{ paddingInline: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={3}>Manage user comments</Title>
      </div>
      <ProTable<IComment>
        rowKey="_id"
        dataSource={data?.results}
        columns={columns}
        pagination={{
          total: data?.totalResults,
          pageSize: queryParams.limit,
          current: queryParams.page,
        }}
        loading={isLoading || updateCommentMutation.isPending}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
        search={{
          labelWidth: "auto",
        }}
        onSubmit={(params: ParamsType) => {
          setQueryParams((prev) => ({ ...prev, page: 1 }));
          setSearchParams({
            dictionary: params.dictionary as string | undefined,
            hanji: params.hanji as string | undefined,
            user: params.user as string | undefined,
            dateRange: params.dateRange as [string, string] | undefined,
          });
        }}
        onReset={() => {
          setQueryParams({
            page: 1,
            limit: 100,
            sortBy: "",
          });
          setSearchParams({});
        }}
      />
    </div>
  );
};

export default CommentManagement;
